import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Package, Eye, EyeOff, RotateCcw, CheckCircle2,
  Flag, CreditCard, Users as UsersIcon, Settings, BarChart3, TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, Legend,
} from 'recharts';
import { superApi } from './SuperAdminAuth';

const SuperCompanyDetailPage = () => {
  const { companyId } = useParams();
  const [tab, setTab] = useState('modules');
  const [moduleState, setModuleState] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadModules = async () => {
    setLoading(true);
    try {
      const r = await superApi.get(`/super/plans/companies/${companyId}/modules`);
      setModuleState(r.data);
    } catch {
      toast.error('Failed to load company modules');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadModules(); }, [companyId]);

  const setOverride = async (module, action) => {
    try {
      await superApi.post(`/super/plans/companies/${companyId}/modules`, { module, action });
      toast.success(`${module}: ${action}`);
      loadModules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const tabs = [
    { key: 'modules', label: 'Modules', icon: Package },
    { key: 'usage', label: 'Usage Analytics', icon: BarChart3 },
    { key: 'plan', label: 'Plan & Billing', icon: CreditCard },
    { key: 'general', label: 'General', icon: Settings },
  ];

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="super-company-detail">
      <Link to="/superadmin/companies" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 mb-4">
        <ArrowLeft className="w-3 h-3" /> Companies
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Building2 className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">
          {moduleState?.company?.name || 'Company'}
        </h1>
        {moduleState?.company?.plan_type && (
          <span className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-300 capitalize">
            {moduleState.company.plan_type} plan
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-800 mb-6">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 ${
              tab === t.key ? 'border-amber-400 text-amber-300' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid={`detail-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'modules' && (
        loading ? <div className="text-slate-500">Loading…</div> :
        <ModulesTab state={moduleState} onSet={setOverride} />
      )}

      {tab === 'usage' && (
        <UsageAnalyticsTab companyId={companyId} />
      )}

      {tab === 'plan' && (
        <ChangePlanTab
          companyId={companyId}
          currentPlan={moduleState?.company?.plan_type}
          onPlanChanged={loadModules}
        />
      )}

      {tab === 'general' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="company-general-panel">
          <h2 className="text-sm font-semibold text-white mb-4">General settings</h2>
          <p className="text-slate-400 text-sm">
            The full legacy settings page (users, integrations, company profile) lives under:
          </p>
          <a
            href={`/super-admin/companies/${companyId}/settings`}
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
          >
            <Settings className="w-4 h-4" /> Open legacy settings →
          </a>
        </div>
      )}
    </div>
  );
};

// --- Modules tab ---------------------------------------------------------
const ModulesTab = ({ state, onSet }) => {
  const { modules = [], plan = {} } = state || {};
  const grouped = useMemo(() => {
    const out = {};
    for (const m of modules) (out[m.group] = out[m.group] || []).push(m);
    return out;
  }, [modules]);

  return (
    <div className="space-y-5">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3 text-sm">
        <Flag className="w-4 h-4 text-amber-400" />
        <div>
          Plan <b className="text-white">{plan?.name}</b> includes <b className="text-white">{(plan?.modules_enabled || []).length}</b> modules by default.
          Use the toggles below to override on a per-company basis.
        </div>
      </div>

      {Object.entries(grouped).map(([group, mods]) => (
        <section key={group} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <header className="px-5 py-3 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">{group}</header>
          <div className="divide-y divide-slate-800">
            {mods.map(m => (
              <div key={m.key} className="px-5 py-3 flex items-center justify-between" data-testid={`company-mod-${m.key}`}>
                <div className="flex items-center gap-3">
                  {m.enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-slate-500" />}
                  <div>
                    <div className="text-sm text-slate-200">{m.label}</div>
                    <div className="text-xs text-slate-500">
                      {m.state === 'plan-default' && <>in plan: {m.in_plan ? <span className="text-emerald-400">yes</span> : <span className="text-slate-400">no</span>}</>}
                      {m.state === 'force-on' && <span className="text-amber-400">force-enabled by super-admin</span>}
                      {m.state === 'force-off' && <span className="text-rose-400">force-disabled by super-admin</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {!m.enabled && (
                    <button onClick={() => onSet(m.key, 'enable')} className="px-3 py-1 rounded bg-emerald-500/10 text-emerald-300 text-xs hover:bg-emerald-500/20" data-testid={`mod-enable-${m.key}`}>
                      <Eye className="w-3 h-3 inline" /> Force enable
                    </button>
                  )}
                  {m.enabled && !m.locked && (
                    <button onClick={() => onSet(m.key, 'disable')} className="px-3 py-1 rounded bg-rose-500/10 text-rose-300 text-xs hover:bg-rose-500/20" data-testid={`mod-disable-${m.key}`}>
                      <EyeOff className="w-3 h-3 inline" /> Force disable
                    </button>
                  )}
                  {m.state !== 'plan-default' && (
                    <button onClick={() => onSet(m.key, 'reset')} className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs" data-testid={`mod-reset-${m.key}`}>
                      <RotateCcw className="w-3 h-3 inline" /> Reset
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default SuperCompanyDetailPage;

// --- Change Plan Tab -----------------------------------------------------
// Lets the super-admin switch a tenant's plan. Pulls available plans from the
// DB-backed /super/plans (same source the Plans editor edits). Selecting a
// plan → confirm dialog → POST /api/shopify-billing/admin/update-plan which
// applies the plan's modules_enabled, integrations, and usage limits.
const ChangePlanTab = ({ companyId, currentPlan, onPlanChanged }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    superApi.get('/super/plans')
      .then(r => { if (!cancelled) setPlans((r.data?.plans || []).filter(p => p.is_active !== false)); })
      .catch(() => { if (!cancelled) toast.error('Failed to load plans'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const applyPlan = async (planId) => {
    if (!planId) return;
    if (planId === currentPlan) {
      toast.info(`${planId} is already the active plan`);
      return;
    }
    const ok = window.confirm(
      `Change plan from "${currentPlan || 'free'}" to "${planId}"?\n\n` +
      `This will replace the tenant's modules, integrations, and usage limits ` +
      `with the new plan's defaults. Per-company module overrides are preserved.`
    );
    if (!ok) return;
    setSaving(true);
    try {
      await superApi.post('/shopify-billing/admin/update-plan', {
        company_id: companyId,
        plan_type: planId,
        reason: reason || `Manual change via Super Admin portal`,
      });
      toast.success(`Plan changed to ${planId}`);
      setSelected(null); setReason('');
      onPlanChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change plan');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">Loading plans…</div>;

  return (
    <div className="space-y-5" data-testid="company-change-plan-panel">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3 text-sm">
        <CreditCard className="w-4 h-4 text-amber-400" />
        <div>
          Current plan: <b className="text-white capitalize">{currentPlan || 'free'}</b>.
          Pick a new plan below to reassign — modules, integrations and usage limits
          will be replaced with the selected plan's defaults.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {plans.map(p => {
          const isCurrent = p.id === currentPlan;
          const isSelected = p.id === selected;
          return (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              disabled={isCurrent}
              data-testid={`plan-card-${p.id}`}
              className={`text-left rounded-xl p-4 border transition ${
                isCurrent ? 'border-emerald-500/60 bg-emerald-500/5 cursor-default' :
                isSelected ? 'border-amber-400 bg-amber-500/10' :
                'border-slate-800 bg-slate-900 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold text-white">{p.name || p.id}</div>
                {isCurrent && <span className="text-[10px] uppercase tracking-wider text-emerald-400">Current</span>}
              </div>
              <div className="text-xs text-slate-400">
                {p.price > 0 ? `$${p.price}/mo` : (p.id === 'free' ? 'Free' : 'Custom pricing')}
              </div>
              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                <div>Orders: {p.orders_limit === -1 ? '∞' : (p.orders_limit ?? '—').toLocaleString()}</div>
                <div>Leads: {p.leads_limit === -1 ? '∞' : (p.leads_limit ?? '—').toLocaleString()}</div>
                <div>Users: {p.users_limit === -1 ? '∞' : (p.users_limit ?? '—')}</div>
                <div>Modules: {(p.modules_enabled || []).length}</div>
                <div>Integrations: {(p.integrations || []).length}</div>
              </div>
            </button>
          );
        })}
      </div>

      {selected && selected !== currentPlan && (
        <div className="bg-slate-900 border border-amber-500/40 rounded-xl p-4 space-y-3" data-testid="change-plan-confirm">
          <div className="text-sm text-slate-200">
            Change to <b className="capitalize">{selected}</b>?
          </div>
          <input
            type="text"
            placeholder="Reason (optional) — e.g. 'Upgraded per customer request'"
            value={reason}
            onChange={e => setReason(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
            data-testid="change-plan-reason"
          />
          <div className="flex gap-2">
            <button
              onClick={() => applyPlan(selected)}
              disabled={saving}
              data-testid="change-plan-apply"
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Applying…' : `Confirm change → ${selected}`}
            </button>
            <button
              onClick={() => { setSelected(null); setReason(''); }}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
            >Cancel</button>
          </div>
        </div>
      )}

      <div className="pt-3 border-t border-slate-800">
        <Link
          to={`/superadmin/billing/${companyId}`}
          className="text-xs text-slate-400 hover:text-amber-400 inline-flex items-center gap-1"
        >
          <CreditCard className="w-3 h-3" /> View full Stripe invoices &amp; payment history →
        </Link>
      </div>
    </div>
  );
};


// --- Usage Analytics Tab -------------------------------------------------
const UsageAnalyticsTab = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    superApi.get(`/super/companies/${companyId}/usage-analytics?days=${range}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load usage analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, range]);

  if (loading) return <div className="text-slate-500">Loading usage analytics…</div>;
  if (!data) return <div className="text-slate-500">No data.</div>;

  const { trend = [], totals_mtd = {}, plan = {} } = data;
  // Total for the window — used to contextualize limit overlay
  const windowTotals = trend.reduce((acc, r) => ({
    orders: acc.orders + (r.orders || 0),
    leads:  acc.leads  + (r.leads  || 0),
    users:  acc.users  + (r.users  || 0),
    api:    acc.api    + (r.api_calls || 0),
  }), { orders: 0, leads: 0, users: 0, api: 0 });

  const plan_orders = plan.orders_limit;
  const plan_leads = plan.leads_limit;
  const plan_users = plan.users_limit;
  // Daily limit overlay = plan monthly limit / 30
  const dailyLine = (limit) => (limit && limit > 0 ? Math.round(limit / 30) : null);

  const LimitCard = ({ label, used, limit, color }) => {
    const pct = limit && limit > 0 ? Math.min(100, Math.round(used / limit * 100)) : null;
    const over = limit && limit > 0 && used > limit;
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid={`usage-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="text-xs text-slate-500 uppercase tracking-wider">{label} (MTD)</div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="text-2xl font-semibold text-white">{used.toLocaleString()}</div>
          <div className="text-xs text-slate-500">
            {limit === -1 ? 'unlimited' : limit != null ? `/ ${limit.toLocaleString()}` : ''}
          </div>
        </div>
        {pct !== null && (
          <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full ${over ? 'bg-rose-500' : pct > 80 ? 'bg-amber-400' : color}`} style={{ width: `${pct}%` }} />
          </div>
        )}
        {over && <div className="mt-1 text-xs text-rose-400">Over plan limit</div>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="company-usage-panel">
      {/* MTD usage vs plan limits */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <LimitCard label="Orders" used={totals_mtd.orders || 0} limit={plan_orders} color="bg-emerald-500" />
        <LimitCard label="Leads" used={totals_mtd.leads || 0} limit={plan_leads} color="bg-sky-500" />
        <LimitCard label="Users" used={totals_mtd.users || 0} limit={plan_users} color="bg-violet-500" />
        <LimitCard label="API calls" used={totals_mtd.api_calls || 0} limit={null} color="bg-amber-500" />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <TrendingUp className="w-4 h-4 text-amber-400" />
          Last {range} days — totals: {windowTotals.orders} orders, {windowTotals.leads} leads, {windowTotals.users} users, {windowTotals.api} api calls
        </div>
        <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1">
          {[7, 30, 90].map(n => (
            <button
              key={n}
              onClick={() => setRange(n)}
              className={`px-2.5 py-1 text-xs rounded ${range === n ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid={`usage-range-${n}`}
            >{n}d</button>
          ))}
        </div>
      </div>

      {/* Trend chart */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3">Daily trend</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11}
                     tickFormatter={(v) => v ? v.slice(5) : ''} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="orders" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="leads"  stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="users"  stroke="#8b5cf6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="api_calls" stroke="#f59e0b" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              {dailyLine(plan_orders) && (
                <ReferenceLine y={dailyLine(plan_orders)} stroke="#10b981" strokeDasharray="4 4"
                               label={{ value: `orders limit ÷30 (${dailyLine(plan_orders)})`, fill: '#10b981', fontSize: 10, position: 'insideTopRight' }} />
              )}
              {dailyLine(plan_leads) && (
                <ReferenceLine y={dailyLine(plan_leads)} stroke="#0ea5e9" strokeDasharray="4 4" />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Reference lines show daily equivalent of the monthly plan limit (limit ÷ 30). Use this
          to spot tenants trending toward an overage — a good signal to reach out with an upgrade.
        </div>
      </div>

      {/* Plan context */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-xs text-slate-400">
        Plan: <span className="text-white font-medium">{plan.name || plan.id || 'free'}</span>
        {' · '}
        Limits: orders {plan_orders === -1 ? '∞' : (plan_orders ?? '—')},
        leads {plan_leads === -1 ? '∞' : (plan_leads ?? '—')},
        users {plan_users === -1 ? '∞' : (plan_users ?? '—')}
      </div>
    </div>
  );
};

